// React
import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'

// Components
import DayPicker from 'ui/components/DayPicker'

// Hooks
import { useSettings } from 'shared/utils/useSettings'
import { CalendarPageContext } from 'ui/components/Picker/Picker.jsx'

// Shared
import moment, { localMomentInUTC } from 'shared/lib/moment'

// Destructure `onBlur` prop to avoid passing it to the `DayPicker` component
// eslint-disable-next-line no-unused-vars
export const DatePicker = ({ onBlur, ...props }) => {
  const fieldValue = moment(props.field.value || '').toJSON()

  const [value, setValue] = useState(fieldValue)
  const { focusedDate, setFocusedDate } = useContext(CalendarPageContext)

  const firstDayOfWeek = useSettings('dates.first_day_of_week', {
    default: 1,
    transformer: (value) => parseInt(value)
  })

  const handleDayClick = (value, modifiers) => {
    if (modifiers.disabled) return

    const { field, forceUTC, handleClose } = props

    value = forceUTC ? localMomentInUTC(value).toJSON() : moment(value).toJSON()

    field.onChange?.(value, true)

    setValue(value)

    // If difference between selected date and focused date is less than number of months that
    // are shown on the calendar - we do not have to change the selection is already visible.
    const selectedMoment = moment(value)
    const focusedMoment = moment(focusedDate)
    if (selectedMoment.diff(focusedMoment, 'months') > props.numberOfMonths) {
      setFocusedDate(new Date(value))
    }

    field.onBlur?.({
      target: {
        name: field.name,
        value
      }
    })

    handleClose()
  }

  const { onMonthChange, ...propsWithoutOnMonthChange } = props
  const handleMonthChange = (newDate) => {
    if (focusedDate.getFullYear() !== newDate.getFullYear() || focusedDate.getMonth() !== newDate.getMonth()) {
      setFocusedDate(newDate)
    }
    onMonthChange?.(newDate)
  }

  if (props.timeOnly) return null

  return (
    <DayPicker
      numberOfMonths={props.numberOfMonths || 1}
      from={value && new Date(value)}
      onDayClick={handleDayClick}
      enableOutsideDaysClick={false}
      firstDayOfWeek={parseInt(firstDayOfWeek)}
      month={focusedDate}
      onMonthChange={handleMonthChange}
      currentSelection={props.mode}
      {...propsWithoutOnMonthChange}
    />
  )
}

DatePicker.propTypes = {
  field: PropTypes.object,
  forceUTC: PropTypes.bool,
  timeOnly: PropTypes.bool,
  handleClose: PropTypes.func,
  numberOfMonths: PropTypes.number,
  onBlur: PropTypes.func,
  onMonthChange: PropTypes.func,
  mode: PropTypes.string
}
